/**
 * PBFCM-PDP Controller
 * Black Friday Campaign - Mobile Product Detail Page JavaScript Controller
 *
 * Handles all user interactions and state management for PBFCM-PDP template
 * Based on patterns from separate-buy-buttons-v4-no-bulk.liquid
 */

// Initialize global namespace
window.PBFCM_PDP = window.PBFCM_PDP || {};

(function(PBFCM_PDP) {
  'use strict';

  // Configuration - All settings and constants
  const CONFIG = {
    // Selling Plans
    STAY_SELLING_PLAN_ID: null, // Will be set from hidden field
    ADD_ON_SUBSCRIPTION_PLAN_ID: null,

    // Prices and Discounts (will be updated from product data)
    SUBSCRIPTION_DISCOUNT: 0.64,
    SHIPPING_COST: 5.00,
    FREE_SHIPPING_THRESHOLD: 50.00,
    BFCM_MODE_ENABLED: false,
    BFCM_DISCOUNT_PERCENT: 0,
    BFCM_DISCOUNT_MULTIPLIER: 1,

    // Product Properties (matches separate-buy-buttons-v4-no-bulk pattern)
    PROPERTIES: {
      FULL_PRICE: "_full_price",
      PRODUCT_TYPE: "_product_type",
      SUBSCRIPTION_PURCHASE: "_is_subscription_purchase",
      ONE_TIME_PURCHASE: "_is_one_time_purchase",
      UPSELL_ITEM: "_is_upsell",
      BFCM_ADD_ON_PROMO_PRICE: "_bfcm_add_on_promo_price",
      BFCM_ADD_ON_FULL_PRICE: "_bfcm_add_on_full_price",
      OTP_DISCOUNT_CODE: "_otp_discount_code",
      SUB_DISCOUNT_CODE: "_sub_discount_code",
      OTP_DISCOUNT_PERCENTAGE: "_otp_discount_percentage",
      SUB_DISCOUNT_PERCENTAGE: "_sub_discount_percentage",
      // Plan a line *would* convert to if upgraded to subscription. Stamped on
      // OTP lines too, so the "_if_upgrade_" prefix keeps it from reading as an
      // active subscription binding (the real binding is item.selling_plan).
      IF_UPGRADE_SELLING_PLAN_ID: "_if_upgrade__selling_plan_id"
    },

    // Element IDs
    IDS: {
      FREQUENCY_MONTHLY: 'PBFCM-frequency-monthly',
      FREQUENCY_ONETIME: 'PBFCM-frequency-onetime',
      RADIO_MONTHLY: 'PBFCM-radio-monthly',
      RADIO_ONETIME: 'PBFCM-radio-onetime',
      CHECKMARK_MONTHLY: 'PBFCM-checkmark-monthly',
      CHECKMARK_ONETIME: 'PBFCM-checkmark-onetime',
      SELECTED_QUANTITY: 'PBFCM-selected-quantity',
      VARIANT_ID: 'PBFCM-variant-id',
      PRODUCT_ID: 'PBFCM-product-id',
      SELLING_PLAN_ID: 'PBFCM-selling-plan-id',
      ADDON_PLAN_ID: 'PBFCM-addon-plan-id',
      BASE_PRICE: 'PBFCM-base-price',
      SUBSCRIPTION_DISCOUNT: 'PBFCM-subscription-discount',
      ADD_TO_CART_BTN: 'PBFCM-add-to-cart-btn',
      CART_LOADING: 'PBFCM-cart-loading',
      COUPON_CODE: 'PBFCM-coupon-code',
      COPY_MESSAGE: 'PBFCM-copy-message',
      BFCM_MODE: 'PBFCM-bfcm-mode-enabled',
      BFCM_DISCOUNT_PERCENT: 'PBFCM-bfcm-discount-percent',
      STRAIGHT_TO_CHECKOUT: 'PBFCM-straight-to-checkout'
    },

    // Class Names
    CLASSES: {
      OPTION_SELECTED: 'PBFCM-PDP-frequency-selector__option--selected',
      QUANTITY_SELECTED: 'PBFCM-PDP-quantity-selector__item--selected',
      SAVINGS_SELECTED: 'PBFCM-PDP-quantity-selector__savings-badge--selected'
    }
  };

  // State management
  const state = {
    frequency: 'monthly', // 'monthly' or 'onetime'
    quantity: 2, // Default quantity
    upsells: {}, // { productId: quantity }
    prices: {
      base: 0,
      subscription: 0,
      onetime: 0,
      shipping: CONFIG.SHIPPING_COST,
      total: 0,
      savings: 0
    },
    variantId: null,
    productId: null,
    sellingPlanId: null
  };
  let upsellDialogModeObserver = null;
  let upsellDialogModeRefreshFrame = null;

  function getUpsellDialogModeState() {
    const liquidDialogMode = document.getElementById('PBFCM-upsell-dialog-mode')?.value === 'true';
    const pdpUpsellEl = document.querySelector('.pdp-upsell-ab-target');
    const pdpGiftEl = document.querySelector('.pdp-gift-ab-target');
    const isPdpHiddenByCSS = (pdpUpsellEl && getComputedStyle(pdpUpsellEl).display === 'none') ||
      (pdpGiftEl && getComputedStyle(pdpGiftEl).display === 'none');

    return {
      liquidDialogMode,
      isPdpHiddenByCSS,
      useUpsellDialog: liquidDialogMode || isPdpHiddenByCSS
    };
  }

  function updateAddToCartButtonText() {
    const button = document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN);
    const buttonText = button?.querySelector('.PBFCM-PDP-cart-summary__button-text');

    if (!button || !buttonText) return;

const defaultText = button.dataset.defaultText || buttonText.textContent.trim();
const { useUpsellDialog } = getUpsellDialogModeState();
const isWelcomeKitTemplate = window.location.pathname.includes('sweepstakes');

const nextText = useUpsellDialog
  ? (isWelcomeKitTemplate ? 'Add To Cart & Claim Gifts' : 'Add To Cart & View Gift')
  : defaultText;

button.dataset.defaultText = defaultText;
buttonText.textContent = nextText;
button.setAttribute('aria-label', nextText);
  }

  function scheduleAddToCartButtonTextUpdate() {
    if (upsellDialogModeRefreshFrame) {
      cancelAnimationFrame(upsellDialogModeRefreshFrame);
    }

    upsellDialogModeRefreshFrame = requestAnimationFrame(() => {
      upsellDialogModeRefreshFrame = null;
      updateAddToCartButtonText();
    });
  }

  function observeUpsellDialogModeChanges() {
    if (upsellDialogModeObserver) {
      upsellDialogModeObserver.disconnect();
    }

    upsellDialogModeObserver = new MutationObserver(() => {
      scheduleAddToCartButtonTextUpdate();
    });

    if (document.head) {
      upsellDialogModeObserver.observe(document.head, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    const pdpUpsellEl = document.querySelector('.pdp-upsell-ab-target');
    const pdpGiftEl = document.querySelector('.pdp-gift-ab-target');

    [pdpUpsellEl, pdpGiftEl].forEach((element) => {
      if (!element) return;

      upsellDialogModeObserver.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    });
  }

  /**
   * Initialize the controller
   */
  function init() {
    console.log('🚀 [PBFCM-PDP] Initializing controller');

    // Load configuration from hidden fields
    loadConfig();

    // Calculate initial prices
    calculateTotals();

    // Set up event listeners
    setupEventListeners();
    updateQuantitySavingsDisplay();
    updateAddToCartButtonText();
    observeUpsellDialogModeChanges();

    // Listen for gift line changes and recalculate totals
    document.addEventListener('pbfcmpdp:gift-line-changed', () => {
      console.log('🎁 [PBFCM-PDP] Gift line changed, recalculating totals');
      recalculateTotalsOnly();
    });

    console.log('✅ [PBFCM-PDP] Controller initialized', state);
  }

  /**
   * Load configuration from hidden fields
   */
  function loadConfig() {
    const variantIdEl = document.getElementById(CONFIG.IDS.VARIANT_ID);
    const productIdEl = document.getElementById(CONFIG.IDS.PRODUCT_ID);
    const sellingPlanIdEl = document.getElementById(CONFIG.IDS.SELLING_PLAN_ID);
    const addonPlanIdEl = document.getElementById(CONFIG.IDS.ADDON_PLAN_ID);
    const basePriceEl = document.getElementById(CONFIG.IDS.BASE_PRICE);
    const subscriptionDiscountEl = document.getElementById(CONFIG.IDS.SUBSCRIPTION_DISCOUNT);
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    const bfcmModeEl = document.getElementById(CONFIG.IDS.BFCM_MODE);
    const bfcmDiscountEl = document.getElementById(CONFIG.IDS.BFCM_DISCOUNT_PERCENT);

    if (variantIdEl) state.variantId = parseInt(variantIdEl.value);
    if (productIdEl) state.productId = parseInt(productIdEl.value);
    if (sellingPlanIdEl) {
      state.sellingPlanId = sellingPlanIdEl.value;
      CONFIG.STAY_SELLING_PLAN_ID = sellingPlanIdEl.value;
    }
    if (addonPlanIdEl) {
      CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID = addonPlanIdEl.value || null;
      console.log('📦 [PBFCM-PDP] Addon plan ID from hidden field:', CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID);
    } else {
      console.warn('⚠️ [PBFCM-PDP] Hidden field PBFCM-addon-plan-id not found');
    }

    // Add warning if addon plan is not set
    if (!CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID) {
      console.warn('⚠️ [PBFCM-PDP] ADD_ON_SUBSCRIPTION_PLAN_ID is not configured - add-on subscriptions will not work correctly');
      console.warn('⚠️ [PBFCM-PDP] Please configure settings.addon_subscription_plan_id in Shopify admin');
    }

    // Load starter metafields
    const starterMultivitaminEl = document.getElementById('PBFCM-starter-multivitamin');
    const starterMagnesiumEl = document.getElementById('PBFCM-starter-magnesium');
    const starterBioticEl = document.getElementById('PBFCM-starter-biotic');

    CONFIG.STARTER_METAFIELDS = {
      multivitamin: starterMultivitaminEl ? starterMultivitaminEl.value : '',
      magnesium: starterMagnesiumEl ? starterMagnesiumEl.value : '',
      biotic: starterBioticEl ? starterBioticEl.value : ''
    };

    console.log('📦 [PBFCM-PDP] Starter metafields loaded:', CONFIG.STARTER_METAFIELDS);

    CONFIG.STARTER_PREVENT_SUBSCRIPTION = {
      multivitamin: document.getElementById('PBFCM-starter-multivitamin-prevent-subscription')?.value || '',
      magnesium: document.getElementById('PBFCM-starter-magnesium-prevent-subscription')?.value || '',
      biotic: document.getElementById('PBFCM-starter-biotic-prevent-subscription')?.value || '',
    };
    console.log('[preventSubscriptionOfMetafieldVariant] Prevent-subscription flags loaded:', CONFIG.STARTER_PREVENT_SUBSCRIPTION);

    // Load bundle name for starter variants
    const bundleNameEl = document.getElementById('PBFCM-bundle-name');
    CONFIG.BUNDLE_NAME = bundleNameEl ? bundleNameEl.value : '';
    console.log('📦 [PBFCM-PDP] Bundle name loaded:', CONFIG.BUNDLE_NAME);

    if (basePriceEl) state.prices.base = parseFloat(basePriceEl.value) / 100;
    if (subscriptionDiscountEl) CONFIG.SUBSCRIPTION_DISCOUNT = parseFloat(subscriptionDiscountEl.value);

    // PBFCM-PDP-frequency-selector path A (when sub_discount_percentage is set):
    // final_monthly_price = variant_price × (1 - pct/100). BFCM does NOT stack —
    // the percentage IS the final discount, see lines 92-98 of that snippet.
    // Mirror that here by (a) overriding SUBSCRIPTION_DISCOUNT to (100 - pct)/100,
    // and (b) setting SUB_PERCENTAGE_FINAL so updatePriceDisplays skips
    // applyBfcmDiscount on the subscription line only. OTP pricing keeps the
    // existing BFCM stack behavior.
    CONFIG.SUB_PERCENTAGE_FINAL = false;
    const subDiscountPercentageEl = document.getElementById('PBFCM-sub-discount-percentage');
    if (subDiscountPercentageEl) {
      const pct = parseFloat(subDiscountPercentageEl.value);
      if (Number.isFinite(pct) && pct > 0 && pct <= 100) {
        CONFIG.SUBSCRIPTION_DISCOUNT = (100 - pct) / 100;
        CONFIG.SUB_PERCENTAGE_FINAL = true;
      }
    }

    if (selectedQuantityEl) state.quantity = parseInt(selectedQuantityEl.value);
    if (bfcmModeEl) {
      const modeValue = bfcmModeEl.value;
      CONFIG.BFCM_MODE_ENABLED = modeValue === 'true' || modeValue === '1';
    }
    if (bfcmDiscountEl) {
      const percentValue = parseFloat(bfcmDiscountEl.value);
      if (!isNaN(percentValue)) {
        CONFIG.BFCM_DISCOUNT_PERCENT = percentValue;
        CONFIG.BFCM_DISCOUNT_MULTIPLIER = Math.max((100 - percentValue) / 100, 0);
      }
    }

    // Load straight-to-checkout configuration
    const straightToCheckoutEl = document.getElementById(CONFIG.IDS.STRAIGHT_TO_CHECKOUT);
    if (straightToCheckoutEl) {
      const stcValue = straightToCheckoutEl.value;
      CONFIG.STRAIGHT_TO_CHECKOUT = stcValue === 'true' || stcValue === '1' || stcValue === 'yes';
    } else {
      CONFIG.STRAIGHT_TO_CHECKOUT = false;
    }
    console.log('📋 [PBFCM-PDP] Straight to checkout:', CONFIG.STRAIGHT_TO_CHECKOUT);

    console.log('📋 [PBFCM-PDP] Config loaded:', {
      variantId: state.variantId,
      productId: state.productId,
      sellingPlanId: state.sellingPlanId,
      basePrice: state.prices.base,
      subscriptionDiscount: CONFIG.SUBSCRIPTION_DISCOUNT,
      bfcmModeEnabled: CONFIG.BFCM_MODE_ENABLED,
      bfcmDiscountPercent: CONFIG.BFCM_DISCOUNT_PERCENT,
      straightToCheckout: CONFIG.STRAIGHT_TO_CHECKOUT
    });
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    // Radio button change listeners
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    if (monthlyRadio) {
      monthlyRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('monthly');
      });
    }

    if (onetimeRadio) {
      onetimeRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('onetime');
      });
    }
  }

  /**
   * Select purchase frequency (monthly subscription or one-time)
   * @param {string} frequency - 'monthly' or 'onetime'
   */
  function selectFrequency(frequency) {
    console.log('🔄 [PBFCM-PDP] Frequency changed to:', frequency);

    state.frequency = frequency;
    const isMonthly = frequency === 'monthly';

    // Update UI
    const monthlyOption = document.getElementById(CONFIG.IDS.FREQUENCY_MONTHLY);
    const onetimeOption = document.getElementById(CONFIG.IDS.FREQUENCY_ONETIME);
    const monthlyCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_MONTHLY);
    const onetimeCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_ONETIME);
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    // Get benefits and badge elements
    const monthlyBenefits = document.getElementById('PBFCM-monthly-benefits');
    const onetimeBenefits = document.getElementById('PBFCM-onetime-benefits');
    const onetimeBadge = document.getElementById('PBFCM-onetime-badge');

    if (isMonthly) {
      // Select monthly
      if (monthlyOption) {
        monthlyOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.remove('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '';
      }
      if (onetimeOption) {
        onetimeOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.remove('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'inline-flex';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'none';
      if (monthlyRadio) monthlyRadio.checked = true;
      if (onetimeRadio) onetimeRadio.checked = false;

      // Show monthly benefits, hide one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.add('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.add('PBFCM-PDP-frequency-selector__badge--hidden');
    } else {
      // Select one-time
      if (monthlyOption) {
        monthlyOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.add('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '1px solid var(--color-helper-outline, #D7EFFA)';
      }
      if (onetimeOption) {
        onetimeOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.add('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'none';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'inline-flex';
      if (monthlyRadio) monthlyRadio.checked = false;
      if (onetimeRadio) onetimeRadio.checked = true;

      // Hide monthly benefits, show one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.remove('PBFCM-PDP-frequency-selector__badge--hidden');
    }

    // Update CSS variable based on selection
    const root = document.documentElement;
    if (frequency === 'onetime') {
      root.style.setProperty('--PBFCM-color-success', '#476ded');
    } else {
      root.style.setProperty('--PBFCM-color-success', '#078942');
    }

    // Refresh the currently-selected quantity tile's inline colors. The tile
    // was rendered server-side with a literal hex via {{ success }} (see
    // PBFCM-PDP-quantity-selector.liquid lines 120 + 130), so the inline style
    // overrides the CSS variable we just swapped. Without this, the active
    // tile's border and savings-badge background keep their old color until
    // the user clicks a quantity, which clears + re-applies via the var.
    const activeQtyTile = document.getElementById(`PBFCM-quantity-${state.quantity}`);
    if (activeQtyTile) {
      activeQtyTile.style.borderColor = 'var(--PBFCM-color-success)';
      const activeBadge = activeQtyTile.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
      if (activeBadge) {
        activeBadge.style.backgroundColor = 'var(--PBFCM-color-success)';
      }
    }

    // Recalculate totals and update upsell prices
    updateQuantitySavingsDisplay();
    updateUpsellPriceDisplays();
    calculateTotals();
  }

  /**
   * Select quantity
   * @param {number} qty - Quantity to select (1-4)
   */
  function selectQuantity(qty) {
    console.log('🔢 [PBFCM-PDP] Quantity changed to:', qty);

    state.quantity = qty;

    // Update hidden field
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    if (selectedQuantityEl) selectedQuantityEl.value = qty;

    // Update UI - remove selection from all items
    for (let i = 1; i <= 4; i++) {
      const item = document.getElementById(`PBFCM-quantity-${i}`);
      if (item) {
        item.classList.remove(CONFIG.CLASSES.QUANTITY_SELECTED);
        item.style.borderColor = '';

        // Also update savings badge
        const savingsBadge = item.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
        if (savingsBadge) {
          savingsBadge.classList.remove(CONFIG.CLASSES.SAVINGS_SELECTED);
          savingsBadge.style.backgroundColor = '';
        }
      }
    }

    // Add selection to chosen item
    const selectedItem = document.getElementById(`PBFCM-quantity-${qty}`);
    if (selectedItem) {
      selectedItem.classList.add(CONFIG.CLASSES.QUANTITY_SELECTED);
      selectedItem.style.borderColor = 'var(--PBFCM-color-success)';

      // Also update savings badge
      const savingsBadge = selectedItem.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
      if (savingsBadge) {
        savingsBadge.classList.add(CONFIG.CLASSES.SAVINGS_SELECTED);
        savingsBadge.style.backgroundColor = 'var(--PBFCM-color-success)';
      }
    }

    // Recalculate totals
    calculateTotals();
  }

  function updateQuantitySavingsDisplay() {
    const isSubscription = state.frequency === 'monthly';
    const savingsElements = document.querySelectorAll('.PBFCM-PDP-quantity-selector__savings-text');
    savingsElements.forEach(element => {
      const nextText = isSubscription ? element.dataset.subscriptionText : element.dataset.onetimeText;
      if (nextText) {
        element.textContent = nextText;
      }
    });
  }

  /**
   * Update upsell product quantity
   * @param {string|number} productId - Product ID
   * @param {number} change - Change in quantity (+1 or -1)
   */
  function updateUpsellQuantity(productId, change) {
    console.log('🎁 [PBFCM-PDP] Updating upsell quantity:', productId, change);

    const currentQty = state.upsells[productId] || 0;
    let newQty = currentQty + change;

    // Get max quantity from data attribute
    const upsellCard = document.querySelector(`[data-product-id="${productId}"]`);
    const upsellData = upsellCard?.querySelector('.PBFCM-upsell-data');
    const maxQty = upsellData ? parseInt(upsellData.dataset.max) : 5;

    // Clamp between 0 and max
    newQty = Math.max(0, Math.min(newQty, maxQty));

    // Update state
    if (newQty === 0) {
      delete state.upsells[productId];
    } else {
      state.upsells[productId] = newQty;
    }

    // Update display
    const qtyDisplay = document.getElementById(`PBFCM-upsell-qty-${productId}`);
    if (qtyDisplay) qtyDisplay.textContent = newQty;

    // Update hidden field
    if (upsellData) upsellData.value = newQty;

    // Update minus button opacity
    const minusBtn = upsellCard?.querySelector('[data-action="decrease"]');
    if (minusBtn) {
      minusBtn.style.opacity = newQty === 0 ? '0.3' : '1';
    }

    console.log('🎁 [PBFCM-PDP] Upsell quantities:', state.upsells);

    // Recalculate totals
    calculateTotals();
  }

  function getUpsellPriceData(upsellData) {
    if (!upsellData) {
      return {
        priceCents: 0,
        comparePriceCents: 0,
        onetimePriceCents: 0,
        price: 0,
        comparePrice: 0,
        onetimePrice: 0
      };
    }

    const rawPriceCents = parseInt(upsellData.dataset.price, 10);
    const rawCompareCents = parseInt(upsellData.dataset.comparePrice || upsellData.dataset.price, 10);
    const rawOnetimeCents = parseInt(upsellData.dataset.onetimePrice, 10);
    const priceCents = Number.isFinite(rawPriceCents) ? rawPriceCents : 0;
    const comparePriceCents = Number.isFinite(rawCompareCents) ? rawCompareCents : priceCents;
    const onetimePriceCents = Number.isFinite(rawOnetimeCents) ? rawOnetimeCents : priceCents;

    return {
      priceCents,
      comparePriceCents,
      onetimePriceCents,
      price: priceCents / 100,
      comparePrice: comparePriceCents / 100,
      onetimePrice: onetimePriceCents / 100
    };
  }

  /**
   * Parse money string to cents
   * @param {string|number} value - Money value (e.g., "$12.34" or "12.34" or 12.34)
   * @returns {number|null} - Value in cents or null if invalid
   */
  function parseMoneyToCents(value) {
      if(value.split("$") && value.split("$")[1]) {
          value = value.split("$")[1]
      }

    if (value === undefined || value === null) return null;
    const textValue = value.toString();
    if (textValue.toUpperCase().includes('FREE')) return 0;
    const normalized = textValue.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (!normalized) return null;
    const floatValue = parseFloat(normalized);
    if (isNaN(floatValue)) return null;

      return Math.round(floatValue * 100);
  }

  /**
   * Read the currently rendered PDP upsell prices from the DOM.
   * This keeps the dialog aligned with whatever price the shopper is seeing inline.
   * @param {string|number} productId - Upsell product ID
   * @returns {{ finalPriceCents: number|null, comparePriceCents: number|null }|null}
   */
  function getLiveUpsellPriceState(productId) {
    const upsellData = document.querySelector(`.PBFCM-upsell-data[data-upsell-id="${productId}"]`);
    const upsellCard = upsellData?.closest('.PBFCM-PDP-upsell-card') ||
      document.querySelector(`.PBFCM-PDP-upsell-card[data-product-id="${productId}"]`);

    if (!upsellData && !upsellCard) return null;

    const finalPriceText = upsellCard?.querySelector('.PBFCM-PDP-upsell-card__price-final [aria-hidden="true"]')?.textContent;
    const comparePriceText = upsellCard?.querySelector('.PBFCM-PDP-upsell-card__price-compare [aria-hidden="true"]')?.textContent;

    const datasetComparePrice = upsellData ? parseInt(upsellData.dataset.comparePrice || '', 10) : NaN;
    const finalPriceCents = parseMoneyToCents(finalPriceText);
    const comparePriceCents = parseMoneyToCents(comparePriceText);

    return {
      finalPriceCents,
      comparePriceCents: comparePriceCents ?? (Number.isFinite(datasetComparePrice) ? datasetComparePrice : null)
    };
  }

  /**
   * Sync dialog upsell product prices with the inline PDP cards.
   * @param {Array} upsellProducts - Dialog upsell products
   * @param {boolean} isSubscription - Whether monthly pricing is active
   * @returns {Array}
   */
  function syncDialogUpsellProductsWithLivePdp(upsellProducts, isSubscription) {
    return upsellProducts.map(product => {
      const livePriceState = getLiveUpsellPriceState(product.id);
      if (!livePriceState) return product;

      const syncedProduct = { ...product };

      if (Number.isFinite(livePriceState.comparePriceCents)) {
        syncedProduct.comparePrice = livePriceState.comparePriceCents;
      }

      if (Number.isFinite(livePriceState.finalPriceCents)) {
        syncedProduct.price = livePriceState.finalPriceCents;

        if (isSubscription) {
          syncedProduct.subscriptionPrice = livePriceState.finalPriceCents;
        } else {
          syncedProduct.onetimePrice = livePriceState.finalPriceCents;
        }
      }

      return syncedProduct;
    });
  }

  /**
   * Get total addon subtotal in cents
   * @returns {number} - Addon subtotal in cents
   */
  function getAddonSubtotalCents() {
    let addonTotal = 0;
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty > 0) {
        const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
        if (upsellData && upsellData.dataset.isAddon === 'true') {
          const priceData = getUpsellPriceData(upsellData);
          addonTotal += Math.round(priceData.price * qty * 100);
        }
      }
    });
    return addonTotal;
  }

  /**
   * Dispatch summary updated event for gift progress component
   * @param {number} totalSaleCents - Total sale price in cents
   * @param {boolean} isFreeShipping - Whether shipping is free
   */
  function dispatchSummaryUpdatedEvent(totalSaleCents, isFreeShipping) {
    document.dispatchEvent(new CustomEvent('pbfcmpdp:summary-updated', {
      detail: {
        allItemsSubtotalCents: totalSaleCents,
        addonSubtotalCents: getAddonSubtotalCents(),
        shippingCents: isFreeShipping ? 0 : CONFIG.SHIPPING_COST * 100
      }
    }));
    console.log('📢 [PBFCM-PDP] Dispatched pbfcmpdp:summary-updated event');
  }

  /**
   * Calculate all prices and update displays
   * Simplified to read from DOM elements with name attributes
   */
  function calculateTotals() {
    console.log('💰 [PBFCM-PDP] Calculating totals from DOM');

    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;

    // Calculate subscription and onetime prices
    state.prices.subscription = basePrice * CONFIG.SUBSCRIPTION_DISCOUNT;
    state.prices.onetime = basePrice;

    // First, ensure all individual items are rendered
    updatePriceDisplays();

    // Wait a tick for DOM to update
    requestAnimationFrame(() => {
      // The gift line carries name="cart-summary-compare-price" on the strikethrough
      // and no matching sale-price counterpart, so its value always shows up as pure
      // savings. Only let it contribute when the gift is actually unlocked
      // (data-active="true" set by PBFCM-PDP-gift-progress.liquid setGiftLine).
      const giftLineEl = document.getElementById('PBFCM-breakdown-gift-line');
      const giftActive = giftLineEl?.dataset.active === 'true';
      const isInactiveGiftLine = (el) =>
        !giftActive && giftLineEl && giftLineEl.contains(el);

      // Sum all compare prices (original prices)
      const compareElements = document.querySelectorAll('[name="cart-summary-compare-price"]');
      let totalCompare = 0;
      compareElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
          console.log("[requestAnimationFrame] cents", cents)

          if (cents !== null) totalCompare += cents;
      });
      console.log("[requestAnimationFrame] compareElements", compareElements)

      // Sum all sale prices (discounted prices)
      const saleElements = document.querySelectorAll('[name="cart-summary-sale-price"]');
      let totalSale = 0;
      saleElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalSale += cents;
      });

      // Update subtotal displays
      const subtotalCompare = document.getElementById('PBFCM-breakdown-subtotal-compare');
      const subtotalFinal = document.getElementById('PBFCM-breakdown-subtotal-final');
      if (subtotalCompare) {
        const formattedCompare = formatMoney(totalCompare / 100);
        subtotalCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
      }
      if (subtotalFinal) {
        const formattedSale = formatMoney(totalSale / 100);
        subtotalFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
      }

      // Update state
      state.prices.total = totalSale / 100;
      state.prices.savings = (totalCompare - totalSale) / 100;

      // Update shipping display - free for subscriptions OR orders over $50
      const shippingCompare = document.getElementById('PBFCM-breakdown-shipping-compare');
      const shippingFinal = document.getElementById('PBFCM-breakdown-shipping-final');
      const isFreeShipping = isSubscription || state.prices.total >= CONFIG.FREE_SHIPPING_THRESHOLD;

      if (isFreeShipping) {
        // Show comparison: crossed-out $5.00 → FREE
        if (shippingCompare) {
          shippingCompare.style.display = '';
          const shippingCost = `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
          shippingCompare.innerHTML = `<span class="sr-only">Original shipping price ${shippingCost}, strikethrough</span><span aria-hidden="true">${shippingCost}</span>`;
        }
        if (shippingFinal) {
          shippingFinal.innerHTML = `<span class="sr-only">Free shipping</span><span aria-hidden="true">FREE</span>`;
        }
      } else {
        // Just show the price, no comparison
        if (shippingCompare) shippingCompare.style.display = 'none';
        if (shippingFinal) {
          const shippingCost = `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
          shippingFinal.innerHTML = `<span class="sr-only">Shipping price ${shippingCost}</span><span aria-hidden="true">${shippingCost}</span>`;
        }
      }

      // Update savings badge
      const savingsText = document.getElementById('PBFCM-breakdown-savings');
      if (savingsText) {
        savingsText.textContent = `You are saving a total of ${formatMoney(state.prices.savings)}!`;
      }

      // Dispatch event for gift progress
      dispatchSummaryUpdatedEvent(totalSale, isFreeShipping);

      console.log('💰 [PBFCM-PDP] Totals calculated:', {
        totalCompare: totalCompare / 100,
        totalSale: totalSale / 100,
        savings: state.prices.savings
      });
    });
  }

  /**
   * Recalculate totals only (without dispatching summary-updated event)
   * Used when gift line changes to avoid infinite loops
   */
  function recalculateTotalsOnly() {
    requestAnimationFrame(() => {
      // Skip gift-line contributions when the gift line isn't actually unlocked —
      // see comment in calculateTotals for why this matters.
      const giftLineEl = document.getElementById('PBFCM-breakdown-gift-line');
      const giftActive = giftLineEl?.dataset.active === 'true';
      const isInactiveGiftLine = (el) =>
        !giftActive && giftLineEl && giftLineEl.contains(el);

      const compareElements = document.querySelectorAll('[name="cart-summary-compare-price"]');
      let totalCompare = 0;
      compareElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalCompare += cents;
      });

      const saleElements = document.querySelectorAll('[name="cart-summary-sale-price"]');
      let totalSale = 0;
      saleElements.forEach(el => {
        if (isInactiveGiftLine(el)) return;
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalSale += cents;
      });

      // Update subtotal displays
      const subtotalCompare = document.getElementById('PBFCM-breakdown-subtotal-compare');
      const subtotalFinal = document.getElementById('PBFCM-breakdown-subtotal-final');
      if (subtotalCompare) {
        const formattedCompare = formatMoney(totalCompare / 100);
        subtotalCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
      }
      if (subtotalFinal) {
        const formattedSale = formatMoney(totalSale / 100);
        subtotalFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
      }

      // Update state
      state.prices.total = totalSale / 100;
      state.prices.savings = (totalCompare - totalSale) / 100;

      // Update savings badge
      const savingsText = document.getElementById('PBFCM-breakdown-savings');
      if (savingsText) {
        savingsText.textContent = `You are saving a total of ${formatMoney(state.prices.savings)}!`;
      }

      console.log('💰 [PBFCM-PDP] Totals recalculated after gift line change:', {
        totalCompare: totalCompare / 100,
        totalSale: totalSale / 100
      });
    });
  }

  /**
   * Update all price displays in the UI
   */
  function updatePriceDisplays() {
    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;
    const unitPrice = isSubscription ? state.prices.subscription : state.prices.onetime;
    const mainProductTotal = unitPrice * state.quantity;
    const mainProductOriginalTotal = basePrice * state.quantity;

    // Update breakdown product line
    const productLabel = document.getElementById('PBFCM-breakdown-product-label');
    const productCompare = document.getElementById('PBFCM-breakdown-product-compare');
    const productFinal = document.getElementById('PBFCM-breakdown-product-final');

    // Get product title from page or default
    const productTitle = document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() || "Kids' Multivitamin";

    if (productLabel) productLabel.textContent = `${productTitle} (${state.quantity}x)`;
    if (productCompare) {
      const formattedCompare = formatMoney(mainProductOriginalTotal);
      productCompare.innerHTML = `<span class="sr-only">Original price ${formattedCompare}, strikethrough</span><span aria-hidden="true">${formattedCompare}</span>`;
    }
    if (productFinal) {
      // Match frequency selector behavior: when sub_discount_percentage is set,
      // the SUBSCRIPTION_DISCOUNT already represents the FINAL combined discount —
      // BFCM must not stack again on the subscription line. OTP keeps the stack.
      const saleAmount = (isSubscription && CONFIG.SUB_PERCENTAGE_FINAL)
        ? mainProductTotal
        : applyBfcmDiscount(mainProductTotal);
      const formattedSale = formatMoney(saleAmount);
      productFinal.innerHTML = `<span class="sr-only">Sale price ${formattedSale}</span><span aria-hidden="true">${formattedSale}</span>`;
    }

    // Update upsell lines in breakdown
    renderUpsellLines();
  }

  /**
   * Render upsell lines in the cart summary breakdown
   */
  function renderUpsellLines() {
    const container = document.getElementById('PBFCM-breakdown-upsells-container');
    if (!container) return;

    // Clear existing upsell lines
    container.innerHTML = '';
    container.style.display = 'none';

    // Check if there are any upsells
    const hasUpsells = Object.keys(state.upsells).some(productId => state.upsells[productId] > 0);
    if (!hasUpsells) return;

    // Get success color from the page
    const successColor = getComputedStyle(document.documentElement).getPropertyValue('--success-color') || '#078942';

    // Render each upsell
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty === 0) return;

      const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
      console.log("[renderUpsellLines] upsell data", upsellData.dataset);
      if (!upsellData) return;

      const upsellTitle = upsellData.dataset.title || 'Upsell Product';
      const priceData = getUpsellPriceData(upsellData);
      const isAddOn = upsellData.dataset.isAddon === 'true';
      const isSubscription = state.frequency === 'monthly';
      const unitPrice = isSubscription ? priceData.price : priceData.onetimePrice;
      const originalPrice = priceData.comparePrice;

      const totalPrice = unitPrice * qty;
      const totalOriginalPrice = originalPrice * qty;
      const discountedTotalPrice = totalPrice;
      console.log("[renderUpsellLines], isSubscription", isSubscription);
      console.log("[renderUpsellLines], unitPrice", unitPrice);
      console.log("[renderUpsellLines], totalPrice", totalPrice);
      console.log("[renderUpsellLines], discountedTotalPrice", discountedTotalPrice);
      console.log("[renderUpsellLines], totalOriginalPrice", totalOriginalPrice);

      // Create upsell line HTML
      const upsellLineHTML = `
        <div class="PBFCM-PDP-cart-summary__line">
          <span class="PBFCM-PDP-cart-summary__line-label PBFCM-PDP-cart-summary__line-label--product">
            ${upsellTitle} (${qty}x)
          </span>
          <div class="PBFCM-PDP-cart-summary__line-price">
            <span class="PBFCM-PDP-cart-summary__line-compare" name="cart-summary-compare-price">
              <span class="sr-only">Original price ${formatMoney(totalOriginalPrice)}, strikethrough</span>
              <span aria-hidden="true">${formatMoney(totalOriginalPrice)}</span>
            </span>
            <span class="PBFCM-PDP-cart-summary__line-final" style="color: ${successColor};" name="cart-summary-sale-price">
              <span class="sr-only">Sale price ${formatMoney(discountedTotalPrice)}, strikethroug</span>
              <span aria-hidden="true">${formatMoney(discountedTotalPrice)}</span>
            </span>
          </div>
        </div>
      `;

      container.insertAdjacentHTML('beforeend', upsellLineHTML);
    });

    container.style.display = '';
  }

  /**
   * Update upsell card price displays based on current frequency
   * Called when frequency changes to show correct discount (subscription vs one-time)
   */
  function updateUpsellPriceDisplays() {
    const isSubscription = state.frequency === 'monthly';
    console.log('[updateUpsellPriceDisplays] Updating upsell prices, isSubscription:', isSubscription);

    document.querySelectorAll('.PBFCM-PDP-upsell-card').forEach(card => {
      const upsellData = card.querySelector('.PBFCM-upsell-data');
      if (!upsellData) return;

      const priceData = getUpsellPriceData(upsellData);
      const displayPrice = isSubscription ? priceData.price : priceData.onetimePrice;

      const priceEl = card.querySelector('.PBFCM-PDP-upsell-card__price-final');
      if (priceEl) {
        const formattedPrice = formatMoney(displayPrice);
        priceEl.innerHTML = `<span class="sr-only">Sale price ${formattedPrice}</span><span aria-hidden="true">${formattedPrice}</span>`;
        console.log('[updateUpsellPriceDisplays] Updated price to:', displayPrice);
      }
    });
  }

  function getBfcmDiscountMultiplier() {
    if (!CONFIG.BFCM_MODE_ENABLED) return 1;
    if (typeof CONFIG.BFCM_DISCOUNT_MULTIPLIER !== 'number' || CONFIG.BFCM_DISCOUNT_MULTIPLIER <= 0) {
      return 1;
    }
    return CONFIG.BFCM_DISCOUNT_MULTIPLIER;
  }

  function applyBfcmDiscount(amount) {
    if (typeof amount !== 'number') return amount;
    const multiplier = getBfcmDiscountMultiplier();
    return amount * multiplier;
  }

  /**
   * Format price as money string
   * @param {number} amount - Amount in dollars
   * @returns {string} Formatted money string
   */
  function formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Copy coupon code to clipboard
   */
  function copyCouponCode() {
    const couponCodeEl = document.getElementById(CONFIG.IDS.COUPON_CODE);
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);

    if (!couponCodeEl) return;

    const code = couponCodeEl.textContent;

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => {
          console.log('📋 [PBFCM-PDP] Coupon code copied:', code);
          showCopyMessage();
        })
        .catch(err => {
          console.error('❌ [PBFCM-PDP] Failed to copy:', err);
          fallbackCopy(code);
        });
    } else {
      fallbackCopy(code);
    }
  }

  /**
   * Fallback copy method for older browsers
   * @param {string} text - Text to copy
   */
  function fallbackCopy(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      console.log('📋 [PBFCM-PDP] Coupon code copied (fallback):', text);
      showCopyMessage();
    } catch (err) {
      console.error('❌ [PBFCM-PDP] Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  /**
   * Show copy success message
   */
  function showCopyMessage() {
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);
    if (!copyMessageEl) return;

    // Show toast with animation
    copyMessageEl.classList.add('is-visible');

    // Hide after 2 seconds
    setTimeout(() => {
      copyMessageEl.classList.remove('is-visible');
    }, 2000);
  }

  /**
   * Create item data structure for cart
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  function shouldPreventSubscription(variantId) {
    if (!variantId) return false;
    console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] checking variantId:', variantId);
    const fields = ['multivitamin', 'magnesium', 'biotic'];
    for (const key of fields) {
      const starterVariant = CONFIG.STARTER_METAFIELDS[key];
      if (!starterVariant) continue;
      if (String(starterVariant) === String(variantId)) {
        const preventFlag = CONFIG.STARTER_PREVENT_SUBSCRIPTION[key];
        console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] matched field:', key, '| preventFlag:', preventFlag);
        if (preventFlag) return true;
      }
    }
    console.log('[preventSubscriptionOfMetafieldVariant] [shouldPreventSubscription] no prevent flag matched');
    return false;
  }

  function createItemData({ variantId, quantity, isSubscription, sellingPlanId = null, properties = {}, isAddon = false }) {
    console.log('📦 [PBFCM-PDP] Creating item data:', { variantId, quantity, isSubscription, isAddon });

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.createItemData === 'function') {
      return window.CartUtilities.createItemData({
        variantId,
        quantity,
        isSubscription,
        properties,
          sellingPlanId,
        isAddon
      });
    }
      console.log('📦 [PBFCM-PDP] Creating item data2:', { variantId, quantity, isSubscription, isAddon });

    // Fallback to local implementation
    const baseProperties = {
      [CONFIG.PROPERTIES.FULL_PRICE]: "true",
      [CONFIG.PROPERTIES.PRODUCT_TYPE]: "rebrand",
      ...properties
    };

    // Mirror cart-utilities.js: stamp PDP discount metadata when the override is present.
    const pdpOverride = (typeof window !== 'undefined' && window.__pdpDiscountOverride) || null;
    if (pdpOverride) {
      const otpCode = pdpOverride.otpCouponCode || pdpOverride.couponCode || '';
      const subCode = pdpOverride.couponCode || '';
      if (otpCode) baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] = String(otpCode);
      if (subCode) baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_CODE] = String(subCode);
      if (Number.isFinite(pdpOverride.discountPercent)) {
        baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE] = String(pdpOverride.discountPercent);
      }
      // Prefer the section's own sub_discount_percentage (frequency selector hidden input)
      // so the line item remembers the exact sub discount that was configured on the PDP
      // it was added from. Falling back to OTP % would re-discount sub lines whenever
      // the merchant uses a sub-only discount without a separate OTP code.
      const sectionSubPctEl = document.getElementById('PBFCM-sub-discount-percentage');
      const sectionSubPctRaw = sectionSubPctEl ? parseFloat(sectionSubPctEl.value) : NaN;
      const sectionSubPct = (Number.isFinite(sectionSubPctRaw) && sectionSubPctRaw > 0 && sectionSubPctRaw <= 100)
        ? sectionSubPctRaw
        : null;
      const subPct = sectionSubPct !== null
        ? sectionSubPct
        : (Number.isFinite(pdpOverride.subDiscountPercent)
            ? pdpOverride.subDiscountPercent
            : (Number.isFinite(pdpOverride.discountPercent) ? pdpOverride.discountPercent : null));
      if (subPct !== null) {
        baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE] = String(subPct);
      }
    }

    const item = {
      id: variantId,
      quantity: quantity,
      properties: baseProperties
    };

    let resolvedPlanId = null;
    if (isSubscription) {
      // If sellingPlanId is explicitly provided, use it
      // Otherwise, use addon plan for add-ons, or Stay AI plan for regular products
      resolvedPlanId = sellingPlanId ||
                      (isAddon ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
      if (resolvedPlanId) {
        item.selling_plan = resolvedPlanId;
      }
      item.properties[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = true;
    } else {
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
    }

    // Stamp _if_upgrade__selling_plan_id even on OTP items, using the plan that would
    // be applied on upgrade-to-sub. Lets cart.json readers skip a /products/{handle}.js
    // lookup. It is upgrade metadata only, not the active subscription binding.
    const planForProperty = resolvedPlanId ||
                            (isAddon ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
    if (planForProperty) {
      item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID] = String(planForProperty);
    }

    console.log('📦 [PBFCM-PDP] Item data created:', item);
    return item;
  }

  /**
   * Show OTP convert dialog and handle user response
   * @returns {Promise<boolean>} - true if user chose to subscribe, false if declined
   */
  function showOTPConvertDialog() {
    return new Promise((resolve) => {
      const dialog = document.querySelector('otp-convert-dialog');

      if (!dialog) {
        console.warn('⚠️ [PBFCM-PDP] OTP Convert Dialog not found, proceeding with OTP');
        resolve(false);
        return;
      }

      dialog.show({
        onSubscribe: () => {
          console.log('🔄 [PBFCM-PDP] User chose to subscribe');
          resolve(true);
        },
        onDecline: () => {
          console.log('🛒 [PBFCM-PDP] User declined subscription, proceeding with OTP');
          resolve(false);
        }
      });
    });
  }

  /**
   * Show upsell dialog and handle user response
   * @param {boolean} isSubscription - Whether this is a subscription purchase
   * @returns {Promise<Object>} - { action: 'checkout'|'decline', selectedUpsells: {} }
   */
  function showUpsellDialog(isSubscription) {
    return new Promise((resolve) => {
      const dialog = document.querySelector('upsell-dialog');

      if (!dialog) {
        console.warn('⚠️ [PBFCM-PDP] Upsell Dialog not found, proceeding without upsells');
        resolve({ action: 'decline', selectedUpsells: {} });
        return;
      }

      // Get upsell products data from JSON script
      const upsellProductsDataEl = document.getElementById('PBFCM-upsell-products-data');
      const giftConfigDataEl = document.getElementById('PBFCM-gift-config-data');

      let upsellProducts = [];
      let giftConfig = null;

      try {
        if (upsellProductsDataEl) {
          upsellProducts = JSON.parse(upsellProductsDataEl.textContent);
        }
        if (giftConfigDataEl) {
          giftConfig = JSON.parse(giftConfigDataEl.textContent);
        }
      } catch (e) {
        console.error('❌ [PBFCM-PDP] Error parsing upsell/gift data:', e);
      }

      upsellProducts = syncDialogUpsellProductsWithLivePdp(upsellProducts, isSubscription);

      // Build main product data
      const mainProduct = {
        id: state.productId,
        variantId: state.variantId,
        title: document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() || 'Product',
        price: Math.round(state.prices.base * 100),
        subscriptionPrice: Math.round(state.prices.subscription * 100),
        comparePrice: Math.round(state.prices.base * 100),
        quantity: state.quantity
      };

      console.log('🎁 [PBFCM-PDP] Showing upsell dialog with:', {
        mainProduct,
        upsellProducts,
        giftConfig,
        isSubscription
      });

      dialog.show({
        mainProduct: mainProduct,
        upsellProducts: upsellProducts,
        giftConfig: giftConfig,
        isSubscription: isSubscription,
        preselectedIndex: giftConfig?.preselectedUpsellIndex ?? -1,
        onCheckout: (selectedUpsells) => {
          console.log('🛒 [PBFCM-PDP] User selected upsells:', selectedUpsells);
          resolve({ action: 'checkout', selectedUpsells: selectedUpsells });
        },
        onDecline: () => {
          console.log('🛒 [PBFCM-PDP] User declined upsells');
          resolve({ action: 'decline', selectedUpsells: {} });
        }
      });
    });
  }

  // Single-flight guard: a second click while a previous ATC is awaiting
  // dialogs or /cart/add.js returns the in-flight promise instead of firing
  // a parallel request that could race on Shopify's cart lock.
  let addToCartInFlight = null;

  /**
   * Main add to cart handler
   * Based on separate-buy-buttons-v4-no-bulk.liquid handleAddToCart function
   */
  async function handleAddToCart() {
    if (addToCartInFlight) {
      console.log('🛒 [PBFCM-PDP] Add to cart already in flight; coalescing duplicate trigger');
      return addToCartInFlight;
    }

    addToCartInFlight = (async () => {
      return _handleAddToCartImpl();
    })().finally(() => {
      addToCartInFlight = null;
    });

    return addToCartInFlight;
  }

  async function _handleAddToCartImpl() {
    console.log('🛒 [PBFCM-PDP] Starting add to cart');

    const isOTP = state.frequency !== 'monthly';

    // Step 1: Show OTP convert dialog if one-time purchase
    if (isOTP) {
      console.log('🔔 [PBFCM-PDP] One-time purchase detected, showing convert dialog');
      const shouldSubscribe = await showOTPConvertDialog();

      if (shouldSubscribe) {
        // User chose to subscribe - switch to monthly and proceed
        selectFrequency('monthly');
      }
      // If declined, proceed with OTP (state.frequency unchanged)
    }

    // Step 2: Check if upsell dialog mode is enabled
    // Either via Liquid flag OR via CSS hiding the PDP elements (A/B test)
    const { liquidDialogMode, isPdpHiddenByCSS, useUpsellDialog } = getUpsellDialogModeState();

    if (isPdpHiddenByCSS && !liquidDialogMode) {
      console.log('🧪 [PBFCM-PDP] A/B variant detected: PDP elements hidden via CSS, triggering dialog mode');
    }

    let dialogUpsells = {};

    if (useUpsellDialog) {
      console.log('🎁 [PBFCM-PDP] Upsell dialog mode enabled, showing dialog');
      const isSubscription = state.frequency === 'monthly';
      const result = await showUpsellDialog(isSubscription);

      if (result.action === 'checkout') {
        dialogUpsells = result.selectedUpsells;
        console.log('🛒 [PBFCM-PDP] User confirmed checkout with upsells:', dialogUpsells);
      } else {
        console.log('🛒 [PBFCM-PDP] User declined upsells, proceeding without them');
      }
    }

    // Step 3: Show loading state and add to cart
    setLoading(true);

    try {
      const isSubscription = state.frequency === 'monthly';
      const items = [];

      // Check if product has starter variants
      const starterVariants = [
        CONFIG.STARTER_METAFIELDS.multivitamin,
        CONFIG.STARTER_METAFIELDS.magnesium,
        CONFIG.STARTER_METAFIELDS.biotic
      ].filter(variantId => variantId && variantId !== '');

      if (starterVariants.length > 0) {
        // Product has starter metafields - ONLY add starter variants, NOT main product
        console.log('🎁 [PBFCM-PDP] Product has starter variants, adding only starters:', starterVariants);
        starterVariants.forEach(variantId => {
          const preventSub = shouldPreventSubscription(parseInt(variantId));
          const effectiveSubscription = preventSub ? false : isSubscription;
          if (preventSub) {
            console.log('[preventSubscriptionOfMetafieldVariant] [handleAddToCart] removing selling_plan from variant:', variantId);
          }

          // Build properties with _bundle_name
          const starterProperties = {
            _bundle_name: CONFIG.BUNDLE_NAME
          };
          if (preventSub) {
            starterProperties._prevent_subscription = 'true';
          }

          items.push(createItemData({
            variantId: parseInt(variantId),
            quantity: state.quantity,
            isSubscription: effectiveSubscription,
            properties: starterProperties
          }));
        });
      } else {
        // No starter variants - add main product as normal
        console.log('🛒 [PBFCM-PDP] No starter variants, adding main product');
        items.push(createItemData({
          variantId: state.variantId,
          quantity: state.quantity,
          isSubscription: isSubscription
        }));
      }

      // Add upsell products - either from dialog (if enabled) or from inline upsells
      const upsellsToAdd = useUpsellDialog ? dialogUpsells : state.upsells;

      // Handle dialog upsells (different data structure)
      if (useUpsellDialog && Object.keys(dialogUpsells).length > 0) {
        // Enforce one free gift per ORDER: if the cart already contains a
        // free-unlock line, don't add another. Only bother checking when a
        // selected upsell actually carries a free unlock.
        let freeGiftAlreadyOnOrder = false;
        const anyFreeUnlock = Object.values(dialogUpsells).some(
          u => u && u.quantity > 0 && u.freeUnlock && u.freeUnlock.variantId
        );
        if (anyFreeUnlock) {
          try {
            const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            if (cartRes.ok) {
              const cartData = await cartRes.json();
              freeGiftAlreadyOnOrder = (cartData.items || []).some(li => {
                const p = li.properties || {};
                return p._free_unlock === 'true' || p._free_unlock === true;
              });
            }
          } catch (e) {
            console.warn('🎁 [PBFCM-PDP] Could not verify existing free gift in cart', e);
          }
        }

        Object.keys(dialogUpsells).forEach(productId => {
          const upsellInfo = dialogUpsells[productId];
          if (upsellInfo && upsellInfo.quantity > 0) {
            // Free "unlocks" attachment → add the separate gifted product as its
            // own line alongside this upsell. Price is driven to $0 via the same
            // promo-price property mechanism the other upsells use (set to 0).
            // NOTE: the actual $0 must be honored by the store's add-on pricing
            // discount/function — verify on a test order before launch.
            // Only ONE free gift per order — quantity is always 1 regardless of
            // how many of the trigger product are added, and we skip entirely if
            // the cart already has a free-unlock line (freeGiftAlreadyOnOrder).
            const freeUnlock = upsellInfo.freeUnlock;
            if (freeUnlock && freeUnlock.variantId && !freeGiftAlreadyOnOrder) {
              const freeProps = {
                [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                _upsell_module: 'pdp_upsells_dialog',
                _page_path: window.location.pathname,
                _free_unlock: 'true',
                _unlocked_by: upsellInfo.title || '',
                [CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE]: '0'
              };
              if (freeUnlock.comparePrice > 0) {
                freeProps[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(freeUnlock.comparePrice));
              }
              // Stamp the trigger's discount code onto the gift line itself. The
              // checkout redirect (subscription-checkout-redirect.liquid) reads
              // this off the cart and merges it with the section's auto-apply
              // code into a single /discount/A,B request, so BOTH apply. Keeping
              // it on the line also means removing the gift removes the code.
              if (freeUnlock.discountCode) {
                freeProps._unlock_discount_code = String(freeUnlock.discountCode);
              }
              // Mark handled so a second selected trigger in the same dialog
              // can't add another free gift.
              freeGiftAlreadyOnOrder = true;
              items.push(createItemData({
                variantId: parseInt(freeUnlock.variantId, 10),
                quantity: 1,
                isSubscription: false,
                properties: freeProps,
                isAddon: true
              }));
            }

            // Bundle / starter-pack upsell → expand into one line item per
            // component, mirroring the main-product starter logic (lines ~1336).
            // Selling plan follows the SAME rule the upsell module uses for every
            // other upsell (add-on → addon plan, else Stay plan). Per-component
            // pricing comes from the dialog data (computed in Liquid).
            const bundleVariants = Array.isArray(upsellInfo.bundleVariants) ? upsellInfo.bundleVariants : [];
            if (bundleVariants.length > 0) {
              let bundlePlanId = null;
              if (isSubscription) {
                const bundleTitle = (upsellInfo.title || '').toLowerCase();
                const bundleIsAddOn = bundleTitle.includes('add-on');
                bundlePlanId = bundleIsAddOn ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID;
                console.log(`📦 [PBFCM-PDP] Bundle upsell "${bundleTitle}" selling plan: ${bundleIsAddOn ? 'ADDON' : 'STAY'} (${bundlePlanId})`);
              }
              bundleVariants.forEach(comp => {
                if (!comp || !comp.id) return;
                const compProps = {
                  [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                  _upsell_module: "pdp_upsells_dialog",
                  _page_path: window.location.pathname,
                  _bundle_name: upsellInfo.bundleName || upsellInfo.title || ''
                };
                const compPromo = isSubscription ? comp.subscriptionPrice : comp.onetimePrice;
                if (compPromo > 0) {
                  compProps[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(compPromo));
                }
                if (comp.fullPrice > 0) {
                  compProps[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(comp.fullPrice));
                }
                const compPrevent = comp.prevent === true;
                if (compPrevent) {
                  compProps._prevent_subscription = 'true';
                  console.log('[preventSubscriptionOfMetafieldVariant] [dialog bundle] removing selling_plan from component:', comp.id);
                }
                items.push(createItemData({
                  variantId: parseInt(comp.id, 10),
                  quantity: upsellInfo.quantity,
                  isSubscription: compPrevent ? false : isSubscription,
                  properties: compProps,
                  isAddon: upsellInfo.isAddon || false,
                  sellingPlanId: compPrevent ? null : bundlePlanId
                }));
              });
              return;
            }

            const upsellProperties = {
              [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
              _upsell_module: "pdp_upsells_dialog",
              _page_path: window.location.pathname
            };
            if (upsellInfo.price > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(upsellInfo.price));
            }
            if (upsellInfo.comparePrice > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(upsellInfo.comparePrice));
            }

            // Determine selling plan ID based on product title
            let sellingPlanId = null;
            if (isSubscription) {
              const upsellTitle = (upsellInfo.title || '').toLowerCase();
              const isAddOnProduct = upsellTitle.includes('add-on');
              sellingPlanId = isAddOnProduct ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID;
              console.log(`📦 [PBFCM-PDP] Selling plan for "${upsellTitle}": ${isAddOnProduct ? 'ADDON' : 'STAY'} (${sellingPlanId})`);
            }

            items.push(createItemData({
              variantId: upsellInfo.variantId,
              quantity: upsellInfo.quantity,
              isSubscription: isSubscription,
              properties: upsellProperties,
              isAddon: upsellInfo.isAddon || false,
              sellingPlanId: sellingPlanId
            }));
          }
        });
      } else {
        // Handle inline upsells (original behavior)
        Object.keys(state.upsells).forEach(productId => {
          const qty = state.upsells[productId];
          if (qty > 0) {
            const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
            if (upsellData) {
              console.log('🛒 [PBFCM-PDP] upsellData', upsellData);

              const variantId = parseInt(upsellData.dataset.variant);
              const priceData = getUpsellPriceData(upsellData);
              const isAddon = upsellData.dataset.isAddon === 'true';
              const upsellProperties = {
                [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true',
                _upsell_module: "pdp_upsells",
                _page_path: window.location.pathname
              };
              if (priceData.priceCents > 0) {
                upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(priceData.priceCents));
              }
              if (priceData.comparePriceCents > 0) {
                upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(priceData.comparePriceCents));
              }

              // Determine selling plan ID based on product title
              let sellingPlanId = null;
              if (isSubscription) {
                const upsellTitle = (upsellData.dataset.upsellTitle || '').toLowerCase();
                const isAddOnProduct = upsellTitle.includes('add-on');
                sellingPlanId = isAddOnProduct ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID;
                console.log(`📦 [PBFCM-PDP] Selling plan for "${upsellTitle}": ${isAddOnProduct ? 'ADDON' : 'STAY'} (${sellingPlanId})`);
              }

              items.push(createItemData({
                variantId: variantId,
                quantity: qty,
                isSubscription: isSubscription,
                properties: upsellProperties,
                isAddon: isAddon,
                sellingPlanId: sellingPlanId
              }));
            }
          }
        });
      }

      console.log('🛒 [PBFCM-PDP] Items to add:', items);

      // Add to cart using existing cart API
      if (typeof window.addToCart === 'function') {
        const response = await window.addToCart({ items });
        console.log('✅ [PBFCM-PDP] Add to cart response:', response);

        // Update cart display
        await updateCartAfterAdd();
      } else {
        console.error('❌ [PBFCM-PDP] window.addToCart not available');
        // Fallback: redirect to cart
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error adding to cart:', error);
      alert('Error adding product to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Update cart display after adding items
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  async function updateCartAfterAdd() {
    console.log('🛒 [PBFCM-PDP] Updating cart display');

    // Check if straight-to-checkout is enabled
    if (CONFIG.STRAIGHT_TO_CHECKOUT) {
      console.log('🚀 [PBFCM-PDP] Straight to checkout enabled, redirecting...');
      if (window.SubscriptionCheckout && typeof window.SubscriptionCheckout.redirectToCheckout === 'function') {
        window.SubscriptionCheckout.redirectToCheckout();
      } else {
        window.location.href = '/checkout';
      }
      return;
    }

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.updateCartAfterAdd === 'function') {
      return window.CartUtilities.updateCartAfterAdd();
    }

    // Fallback to local implementation
    // Open the cart drawer FIRST, independent of the /cart.js fetch below. The
    // add already succeeded; gating the open behind that fetch meant slow/flaky
    // mobile data delayed the open, or a failed fetch threw and skipped it.
    if (typeof toggleCartOpen === 'function') {
      console.log('🛒 [PBFCM-PDP] Opening cart drawer');
      toggleCartOpen();

      // Focus the checkout button after cart opens for accessibility
      setTimeout(() => {
        const checkoutButton = document.getElementById('checkoutButton');

        if (checkoutButton && !checkoutButton.disabled) {
          checkoutButton.focus();
          console.log('♿ [PBFCM-PDP] Focused checkout button for accessibility');

          // Announce to screen readers
          if (typeof announceToScreenReader === 'function') {
            announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
          }
        } else {
          // Fallback: focus close button if checkout is disabled
          const cart = document.querySelector('#main-nav');
          const closeButton = cart?.querySelector('.js-menu__close');

          if (closeButton) {
            closeButton.focus();
            console.log('♿ [PBFCM-PDP] Checkout disabled, focused close button');
          }
        }
      }, 150);
    }

    try {
      // Best-effort: fetch the latest cart and render it into the open drawer.
      const cartResponse = await fetch('/cart.js');
      const updatedCart = await cartResponse.json();

      console.log('🛒 [PBFCM-PDP] Updated cart:', updatedCart);

      // Update cart display
      if (typeof updateCartDisplay === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart display');
        updateCartDisplay(updatedCart);
      }

      // Update cart count
      if (typeof updateCartCount === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart count:', updatedCart.item_count);
        updateCartCount(updatedCart.item_count);
      }

      // Dispatch cart:updated event for gift progress and other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      console.log('📢 [PBFCM-PDP] Dispatched cart:updated event');
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error updating cart display:', error);
    }
  }

  /**
   * Try Before You Buy - Add to cart handler
   * Uses CartUtilities.addToCartTBYB
   */
  async function handleAddToCartTBYB() {
    console.log('🧪 [PBFCM-PDP] Starting TBYB add to cart');

    // Show loading state
    setLoading(true);

    try {
      const isSubscription = state.frequency === 'monthly';

      // Check if CartUtilities is available
      if (window.CartUtilities && typeof window.CartUtilities.addToCartTBYB === 'function') {
        await window.CartUtilities.addToCartTBYB({
          variantId: state.variantId,
          quantity: state.quantity,
          isSubscription: isSubscription,
          productId: state.productId
        });
      } else {
        console.error('❌ [PBFCM-PDP] CartUtilities.addToCartTBYB not available');
        alert('TBYB functionality is not available. Please try regular add to cart.');
      }
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error adding TBYB to cart:', error);
      alert('Error adding product to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Set loading state
   * @param {boolean} loading - Whether loading or not
   */
  function setLoading(loading) {
    const btn = document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN);
    const loadingEl = document.getElementById(CONFIG.IDS.CART_LOADING);

    if (btn) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.7' : '1';
      btn.style.cursor = loading ? 'not-allowed' : 'pointer';
    }

    if (loadingEl) {
      loadingEl.style.display = loading ? 'flex' : 'none';
    }
  }

  // Expose public API
  PBFCM_PDP.init = init;
  PBFCM_PDP.selectFrequency = selectFrequency;
  PBFCM_PDP.selectQuantity = selectQuantity;
  PBFCM_PDP.updateUpsellQuantity = updateUpsellQuantity;
  PBFCM_PDP.calculateTotals = calculateTotals;
  PBFCM_PDP.handleAddToCart = handleAddToCart;
  PBFCM_PDP.handleAddToCartTBYB = handleAddToCartTBYB;
  PBFCM_PDP.refreshUpsellDialogMode = scheduleAddToCartButtonTextUpdate;
  PBFCM_PDP.copyCouponCode = copyCouponCode;
  PBFCM_PDP.showOTPConvertDialog = showOTPConvertDialog;
  PBFCM_PDP.showUpsellDialog = showUpsellDialog;
  PBFCM_PDP.getState = () => ({ ...state }); // For debugging

})(window.PBFCM_PDP);

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.PBFCM_PDP.init);
} else {
  window.PBFCM_PDP.init();
}
